import cn from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import type { ReactNode } from 'react'

import { Translation } from 'cng-common/features/translations'

import type { IFinancialCockpitPendingAction } from '../../../../api/types'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { usePendingActionInvocation } from '../../../cockpitPendingActions/hooks/usePendingActionInvocation'
import {
  getPendingActionKey,
  PENDING_ACTIONS_UNCATEGORIZED,
} from '../../../cockpitPendingActions/lib'
import { useAttentionDetailExit } from '../../hooks/useAttentionDetailExit'

import styles from './AttentionStatusActionRows.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.pendingActions

interface IAttentionStatusActionRowsProps {
  /** The plan the rows act against; every dialog they open is scoped to it. */
  offering: ProgramTypes
  actions: IFinancialCockpitPendingAction[]
  /**
   * Rules the first row off from what is above it. A caller that draws several of these blocks in
   * a row — one per offering — sets it on all but the first, so the blocks read as one list rather
   * than as lists that happen to touch.
   */
  showLeadingDivider?: boolean
  /** The row's two lines in place of the action's title and category, for a section whose rows
   *  name something the action does not: the SOB card leads with the category the action is for. */
  renderLines?: (action: IFinancialCockpitPendingAction) => { title: ReactNode; detail: ReactNode }
  className?: string
}

/**
 * The card row of the attention-status sections: an action stated by what it belongs to rather
 * than by how urgent it is, with the one control the reader can answer it with.
 *
 * The row is not the pending-actions tile's. The tile lists a working queue — priority dot first,
 * the action's own title as the line, and a control only where the reader owns the action. A card
 * is read by someone who opened a status to see what is holding it: the action leads the line and
 * the category it belongs to follows under it at a smaller size.
 *
 * The whole row opens its action, the way the plan review popup does — every row, whoever owns it,
 * with an arrow at its end saying so. A row somebody else owns carries no reminder of its own:
 * reminding that side is one act for the whole list, so it is offered once above the rows rather
 * than repeated on every one of them.
 *
 * Acting on a row is `usePendingActionInvocation`'s, which the tile calls too: the dialogs, the
 * refresh behind a confirmed action and the blocked-by lock are the action's, wherever it is drawn.
 * The hook is called once per list and its dialogs mounted once beside the rows — which is why this
 * draws a whole offering's rows rather than a single one, and why a caller with actions of several
 * offerings calls it once per offering instead of mixing them into one list.
 */
export const AttentionStatusActionRows = ({
  offering,
  actions,
  showLeadingDivider = false,
  renderLines,
  className,
}: IAttentionStatusActionRowsProps) => {
  const { selectAction, isActionBusy, renderBlockedTooltip, modals } =
    usePendingActionInvocation(offering)
  /* A row that carries a link rather than a dialog takes the reader off this card, so the card
     closes behind them instead of standing over the place they were taken to. */
  const exitDetail = useAttentionDetailExit()

  if (!actions.length) {
    return null
  }

  return (
    <div
      className={cn(styles.root, { [styles.showLeadingDivider]: showLeadingDivider }, className)}
    >
      {actions.map((action) => {
        const isBlocked = Boolean(action.ActionBlockedBy)
        const lines = renderLines?.(action)
        const content = lines ? (
          <div className={styles.content}>
            <span className={styles.action}>{lines.title}</span>
            <span className={styles.category}>{lines.detail}</span>
          </div>
        ) : (
          <div className={styles.content}>
            <span className={styles.action}>
              <Translation span text={action.ActionTitle} />
              {action.ActionDescription && (
                <>
                  <span className={styles.separator}>·</span>
                  <Translation span text={action.ActionDescription} />
                </>
              )}
            </span>

            {/* The category under the action, so a section no longer needs a sub-header per
              category: an action that names none keeps the same neutral word the tile groups it
              under rather than losing its second line. */}
            <span className={styles.category}>
              <Translation
                span
                text={`${KEYS.category}${action.ActionCategory || PENDING_ACTIONS_UNCATEGORIZED}`}
              />
            </span>
          </div>
        )

        /* A blocked row is not a control: it keeps its place in the list and wears the lock that
           says which action has to land before it can be opened. */
        if (isBlocked) {
          return (
            <div
              key={getPendingActionKey(action, offering)}
              className={cn(styles.row, styles.row__blocked)}
            >
              {content}

              <span className={styles.end}>
                {renderBlockedTooltip(action.ActionBlockedBy, styles.lockIcon)}
              </span>
            </div>
          )
        }

        return (
          <button
            key={getPendingActionKey(action, offering)}
            type="button"
            className={cn(styles.row, styles.row__action)}
            disabled={isActionBusy(action)}
            onClick={() => {
              if (selectAction(action)) {
                exitDetail()
              }
            }}
          >
            {content}

            <span className={styles.end} aria-hidden>
              <span className={styles.arrow}>→</span>
            </span>
          </button>
        )
      })}

      {modals}
    </div>
  )
}

AttentionStatusActionRows.displayName = 'AttentionStatusActionRows'
